import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { priceAlerts } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Bell, BellOff, TrendingDown, Sparkles, Trash2, Search, Calendar } from 'lucide-react';

const statusColor = {
  active: 'bg-[#E8F5E9] text-[#1B5E20]',
  expired: 'bg-muted text-ink/60',
  fired: 'bg-[#FFF3E0] text-[#9A4500]',
};

export default function MyPriceAlerts() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = async () => {
    try { setItems(await priceAlerts.mine()); } catch { setItems([]); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'customer') { nav('/'); return; }
    load();
    /* eslint-disable-next-line */
  }, [user, loading]);

  const cancel = async (id) => {
    try {
      await priceAlerts.cancel(id);
      toast.success('Alert cancelled');
      load();
    } catch { toast.error('Could not cancel'); }
  };

  if (loading || items === null) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink/60">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#FFF3E0] text-[#9A4500] grid place-items-center"><Bell className="w-6 h-6"/></div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#9A4500]">Smart price watch</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">My Price Alerts</h1>
          <p className="text-ink/70 mt-1 text-sm">We watch every store for you and ping the moment a price drops — over 7, 10, 15 or 30 days.</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card-soft p-12 text-center mt-8">
          <Bell className="w-12 h-12 mx-auto text-ink/30"/>
          <div className="font-heading font-bold text-xl mt-3">No price alerts yet</div>
          <p className="text-ink/60 mt-1 text-sm">Search for any item, then tap "Notify me on price drop".</p>
          <Link to="/search" className="btn-primary inline-flex mt-5"><Search className="w-4 h-4"/>Start watching</Link>
        </div>
      ) : (
        <div className="mt-7 grid gap-4">
          {items.map(a => <AlertRow key={a.id} alert={a} onCancel={cancel}/>)}
        </div>
      )}
    </div>
  );
}

function AlertRow({ alert: a, onCancel }) {
  const fired = (a.fires || []).length > 0;
  const lastFire = fired ? a.fires[a.fires.length - 1] : null;
  return (
    <article data-testid={`price-alert-${a.id}`} className="card-soft p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-xl text-ink">{a.product_query}</h3>
            <span className={`pill !text-[10px] capitalize ${statusColor[a.status] || ''}`}>{a.status}</span>
          </div>
          <div className="text-xs text-ink/60 mt-1 inline-flex items-center gap-1.5"><Calendar className="w-3 h-3"/>Window: <b>{a.window_days} days</b> · expires {new Date(a.expires_at).toLocaleDateString()}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-ink/50">Baseline</div>
          <div className="font-heading font-bold text-xl tabular-nums">₹{a.baseline_price ?? '—'}</div>
        </div>
      </div>

      {fired ? (
        <div data-testid={`price-alert-fire-${a.id}`} className="mt-4 rounded-xl border-2 border-[#1B5E20] bg-[#F1F8F2] p-4">
          <div className="flex items-center gap-2 text-[#1B5E20] text-xs uppercase tracking-[0.2em] font-bold">
            <Sparkles className="w-3.5 h-3.5"/>Price dropped!
          </div>
          <div className="mt-2 flex items-baseline gap-3 flex-wrap">
            <div className="font-heading font-black text-3xl text-[#1B5E20]">₹{lastFire.new_price}</div>
            <div className="text-sm text-ink/60 line-through">₹{lastFire.old_price}</div>
            <div className="pill !text-[10px] !bg-[#E8F5E9] !text-[#1B5E20] inline-flex items-center gap-1"><TrendingDown className="w-3 h-3"/>save ₹{lastFire.drop_inr} · {lastFire.drop_pct}% off</div>
          </div>
          <Link to={`/search?q=${encodeURIComponent(a.product_query)}`} data-testid={`alert-visit-${a.id}`} className="btn-primary mt-3 inline-flex">Visit BharatCart →</Link>
        </div>
      ) : (
        <div className="mt-3 text-sm text-ink/60">
          Lowest current price across stores: <b>{a.current_min_price != null ? `₹${a.current_min_price}` : '—'}</b>.
          We're watching — you'll be pinged the moment it drops below your baseline.
        </div>
      )}

      <div className="mt-3 flex items-center gap-3 text-[11px] text-ink/60 flex-wrap">
        <span>Ping on:</span>
        {a.notify_sms && <span className="pill !text-[10px] !bg-[#E3F2FD] !text-[#0D47A1]">SMS</span>}
        {a.notify_whatsapp && <span className="pill !text-[10px] !bg-[#E8F5E9] !text-[#1B5E20]">WhatsApp</span>}
        <span className="text-ink/40 ml-auto">Last checked {a.last_checked_at ? new Date(a.last_checked_at).toLocaleString() : '—'}</span>
        {a.status === 'active' && (
          <button data-testid={`cancel-alert-${a.id}`} onClick={() => onCancel(a.id)} className="text-xs text-destructive inline-flex items-center gap-1 hover:underline">
            <BellOff className="w-3 h-3"/>Cancel
          </button>
        )}
        {a.status !== 'active' && (
          <button data-testid={`delete-alert-${a.id}`} onClick={() => onCancel(a.id)} className="text-xs text-ink/40 inline-flex items-center gap-1 hover:underline">
            <Trash2 className="w-3 h-3"/>Remove
          </button>
        )}
      </div>
    </article>
  );
}
